<template>
  <div class="ncrm-external-forms-settings d-flex flex-column ga-4">
    <v-alert type="info">
      {{ $t("ncrm.workflows.nodeSettings.externalForms.bestPractice") }}
    </v-alert>

    <div class="d-flex align-center justify-space-between flex-wrap ga-2">
      <div>
        <div class="text-body-2 font-weight-semibold ncrm-text-strong">
          {{ $t("ncrm.workflows.nodeSettings.externalForms.formsTitle") }}
        </div>
        <div class="text-caption text-medium-emphasis mt-1">
          {{ $t("ncrm.workflows.nodeSettings.externalForms.formsHint") }}
        </div>
      </div>
      <v-btn size="small" color="primary" variant="tonal" @click="startCreate">
        <v-icon start>mdi-plus</v-icon>
        {{ $t("ncrm.workflows.nodeSettings.externalForms.newForm") }}
      </v-btn>
    </div>

    <v-progress-linear v-if="loading" indeterminate color="primary" height="2" />
    <v-alert v-if="error" type="error" density="compact">{{ error }}</v-alert>
    <v-alert v-if="success" type="success" density="compact">{{ success }}</v-alert>

    <NcrmWorkflowExternalFormPicker
      v-if="widgets.length"
      v-model="selectedWidgetId"
      :widgets="widgets"
      :select-menu-props="selectMenuProps"
      @update:model-value="selectWidget"
    />

    <v-row dense>
      <v-col cols="12" md="7">
        <v-text-field
          v-model="form.name"
          :label="$t('ncrm.workflows.nodeSettings.externalForms.name')"
          hide-details="auto"
          @blur="ensureFormKey"
        />
      </v-col>
      <v-col cols="12" md="5">
        <v-text-field
          v-model="form.form_key"
          :label="$t('ncrm.workflows.nodeSettings.externalForms.formKey')"
          hide-details="auto"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-select
          v-model="form.endpoint_id"
          :items="endpointOptions"
          item-title="label"
          item-value="value"
          :label="$t('ncrm.workflows.nodeSettings.externalForms.endpoint')"
          hide-details="auto"
          :menu-props="selectMenuProps"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-select
          v-model="form.status"
          :items="statusOptions"
          item-title="label"
          item-value="value"
          :label="$t('ncrm.workflows.nodeSettings.externalForms.status')"
          hide-details="auto"
          :menu-props="selectMenuProps"
        />
      </v-col>
      <v-col cols="12">
        <v-select
          v-model="form.rendererKey"
          :items="rendererOptions"
          item-title="label"
          item-value="value"
          :label="$t('ncrm.workflows.nodeSettings.externalForms.formType')"
          hide-details="auto"
          :menu-props="selectMenuProps"
          @update:model-value="syncRendererKey"
        />
      </v-col>
    </v-row>

    <v-alert v-if="isInlineRenderer" type="info" density="compact">
      {{ $t(`ncrm.workflows.nodeSettings.externalForms.${modeHintKey}`) }}
    </v-alert>

    <v-divider />

    <NcrmWorkflowExternalFormEditor
      v-model:origins-text="originsText"
      v-model:include-pages-text="includePagesText"
      v-model:exclude-pages-text="excludePagesText"
      :form="form"
      :inline-mode="isInlineRenderer"
      :file-request-mode="isFileRequestRenderer || isDataCollectionRenderer"
      :repeat-mode-options="repeatModeOptions"
      :select-menu-props="selectMenuProps"
    />

    <NcrmWorkflowFileRequestContentEditor v-if="isFileRequestRenderer" :form="form" />
    <NcrmWorkflowDataCollectionEditor v-if="isDataCollectionRenderer" :form="form" :select-menu-props="selectMenuProps" @catalog-refreshed="refreshPrivacyStatuses" />
    <v-divider />

    <NcrmWorkflowExternalFormNotifications :form="form" />
    <v-divider />

    <NcrmWorkflowExternalFormPrivacy v-if="!isDataCollectionRenderer" :form="form" :select-menu-props="selectMenuProps" @catalog-refreshed="refreshPrivacyStatuses" />

    <v-alert v-if="embed" type="success" density="compact">
      <div class="text-caption mb-1">{{ $t(`ncrm.workflows.nodeSettings.externalForms.${embed.labelKey}`) }}</div>
      <code class="ncrm-external-forms-settings__embed">{{ embed.script }}</code>
    </v-alert>

    <div class="ncrm-external-forms-settings__actions">
      <v-btn
        variant="tonal"
        color="primary"
        :disabled="!canPreviewInline"
        @click="previewInlineForm"
      >
        <v-icon start>mdi-eye-outline</v-icon>
        {{ $t(`ncrm.workflows.nodeSettings.externalForms.${isInlineRenderer ? "previewInline" : "previewPopup"}`) }}
      </v-btn>
      <v-btn variant="text" @click="startCreate">
        {{ $t("ncrm.workflows.nodeSettings.externalForms.resetDraft") }}
      </v-btn>
      <v-btn color="primary" :loading="saving" :disabled="saveDisabled" @click="saveWidget">
        <v-icon start>mdi-content-save-outline</v-icon>
        {{ $t("ncrm.workflows.nodeSettings.externalForms.saveForm") }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import {
  buildExternalFormWidgetPayload,
  createExternalFormWidget,
  createExternalFormWidgetDraft,
  EXTERNAL_FORM_RENDERER_KEYS,
  fetchExternalFormWidgets,
  formatExternalFormOriginsText,
  INLINE_FILE_REQUEST_RENDERER_KEY,
  normalizeExternalFormWidget,
  parseExternalFormOriginsText,
  slugifyExternalFormKey,
  updateExternalFormWidget,
} from "@/Utile/ncrmExternalFormWidgets.mjs";
import { PRIVACY_ISSUE_CODES, refreshPrivacyStatuses as refreshSavedStatuses } from "@/Utile/ncrmExternalFormPrivacy.mjs";
import {
  DATA_COLLECTION_RENDERER_KEY, DEFAULT_EXTERNAL_FORM_RENDERER_KEY, INLINE_RENDERER_KEYS, RENDERER_EMBEDS, RENDERER_WIDGET_PATHS,
} from "@/Utile/ncrmExternalFormRenderers.mjs";
import NcrmWorkflowDataCollectionEditor from "./NcrmWorkflowDataCollectionEditor.vue";
import NcrmWorkflowExternalFormEditor from "./NcrmWorkflowExternalFormEditor.vue";
import NcrmWorkflowExternalFormNotifications from "./NcrmWorkflowExternalFormNotifications.vue";
import NcrmWorkflowExternalFormPicker from "./NcrmWorkflowExternalFormPicker.vue";
import NcrmWorkflowExternalFormPrivacy from "./NcrmWorkflowExternalFormPrivacy.vue";
import NcrmWorkflowFileRequestContentEditor from "./NcrmWorkflowFileRequestContentEditor.vue";

export default {
  name: "NcrmWorkflowExternalFormsSettings",
  components: {
    NcrmWorkflowDataCollectionEditor,
    NcrmWorkflowExternalFormEditor,
    NcrmWorkflowExternalFormNotifications,
    NcrmWorkflowExternalFormPicker,
    NcrmWorkflowExternalFormPrivacy,
    NcrmWorkflowFileRequestContentEditor,
  },
  props: {
    workflowInstanceId: {
      type: String,
      default: "",
    },
    endpoints: {
      type: Array,
      default: () => [],
    },
    selectMenuProps: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ["saved"],
  data() {
    return {
      widgets: [],
      selectedWidgetId: null,
      form: createExternalFormWidgetDraft(),
      originsText: "",
      includePagesText: "*",
      excludePagesText: "",
      loading: false,
      saving: false,
      error: "",
      success: "",
    };
  },
  computed: {
    endpointOptions() {
      return this.endpoints.map((endpoint) => ({
        value: endpoint.id,
        label: `${endpoint.name || endpoint.endpoint_key} · ${endpoint.entity_scope || "-"}`,
      }));
    },
    statusOptions() {
      return ["draft", "active", "paused", "archived"].map((status) => ({
        value: status,
        label: this.$t(`ncrm.workflows.nodeSettings.externalForms.statuses.${status}`),
      }));
    },
    repeatModeOptions() {
      return ["always", "session", "daily", "after_hours", "never_after_submit"].map((mode) => ({
        value: mode,
        label: this.$t(`ncrm.workflows.nodeSettings.externalForms.repeatModes.${mode}`),
      }));
    },
    rendererOptions() {
      return EXTERNAL_FORM_RENDERER_KEYS.map((rendererKey) => ({
        value: rendererKey,
        label: this.$t(`ncrm.workflows.nodeSettings.externalForms.formTypes.${rendererKey}`),
      }));
    },
    saveDisabled() {
      return !String(this.form.name || "").trim() ||
        !String(this.form.form_key || "").trim() ||
        !this.form.endpoint_id ||
        this.saving;
    },
    isInlineRenderer() { return INLINE_RENDERER_KEYS.includes(this.form.rendererKey); },
    isFileRequestRenderer() { return this.form.rendererKey === INLINE_FILE_REQUEST_RENDERER_KEY; },
    isDataCollectionRenderer() { return this.form.rendererKey === DATA_COLLECTION_RENDERER_KEY; },
    // Codul de integrare al tipului ales (`RENDERER_EMBEDS`).
    embed() {
      const [labelKey, field] = RENDERER_EMBEDS[this.form.rendererKey] || RENDERER_EMBEDS[DEFAULT_EXTERNAL_FORM_RENDERER_KEY];
      return this.form[field] ? { labelKey, script: this.form[field] } : null;
    },
    modeHintKey() {
      return this.isFileRequestRenderer ? "fileRequestModeHint" : this.isDataCollectionRenderer ? "dataCollection.modeHint" : "inlineModeHint";
    },
    canPreviewInline() {
      return Boolean(this.form.id && this.form.form_key && this.form.status === "active");
    },
  },
  watch: {
    workflowInstanceId: {
      immediate: true,
      handler() {
        this.startCreate();
        this.fetchWidgets();
      },
    },
    endpoints: {
      immediate: true,
      handler() {
        if (!this.form.endpoint_id && this.endpoints[0]?.id) {
          this.form.endpoint_id = this.endpoints[0].id;
        }
      },
    },
  },
  methods: {
    refreshPrivacyStatuses() { return refreshSavedStatuses(this.widgets, () => fetchExternalFormWidgets(this.workflowInstanceId)); },
    async fetchWidgets() {
      if (!this.workflowInstanceId) return;
      this.loading = true;
      this.error = "";
      try {
        const { data } = await fetchExternalFormWidgets(this.workflowInstanceId);
        this.widgets = Array.isArray(data?.widgets)
          ? data.widgets.map((widget) => normalizeExternalFormWidget(widget, {
              workflowInstanceId: this.workflowInstanceId,
              endpointId: this.endpoints[0]?.id || null,
            }))
          : [];
        if (!this.selectedWidgetId && this.widgets[0]) {
          this.selectWidget(this.widgets[0].id);
        }
      } catch (error) {
        this.widgets = [];
        this.error = error?.response?.data?.error || this.$t("ncrm.workflows.nodeSettings.externalForms.loadError");
      } finally {
        this.loading = false;
      }
    },
    startCreate() {
      this.selectedWidgetId = null;
      this.form = createExternalFormWidgetDraft({
        workflowInstanceId: this.workflowInstanceId,
        endpointId: this.endpoints[0]?.id || null,
      });
      this.form.name = this.$t("ncrm.workflows.nodeSettings.externalForms.defaultName");
      this.form.form_key = slugifyExternalFormKey(this.form.name);
      this.syncRendererKey(this.form.rendererKey);
      this.syncTextFields();
      this.success = "";
      this.error = "";
    },
    selectWidget(widgetId) {
      const widget = this.widgets.find((item) => Number(item.id) === Number(widgetId));
      if (!widget) return;
      this.selectedWidgetId = widget.id;
      this.form = normalizeExternalFormWidget(widget, {
        workflowInstanceId: this.workflowInstanceId,
        endpointId: this.endpoints[0]?.id || null,
      });
      this.syncTextFields();
      this.success = "";
      this.error = "";
    },
    syncTextFields() {
      this.originsText = formatExternalFormOriginsText(this.form.allowedOrigins);
      this.includePagesText = formatExternalFormOriginsText(this.form.displayConfig.includePages);
      this.excludePagesText = formatExternalFormOriginsText(this.form.displayConfig.excludePages);
    },
    syncArraysFromText() {
      this.form.allowedOrigins = parseExternalFormOriginsText(this.originsText);
      this.form.displayConfig.includePages = parseExternalFormOriginsText(this.includePagesText);
      this.form.displayConfig.excludePages = parseExternalFormOriginsText(this.excludePagesText);
      if (!this.form.displayConfig.includePages.length) this.form.displayConfig.includePages = ["*"];
      this.syncRendererKey(this.form.rendererKey);
    },
    ensureFormKey() {
      if (!String(this.form.form_key || "").trim()) {
        this.form.form_key = slugifyExternalFormKey(this.form.name);
      }
    },
    syncRendererKey(value) {
      const rendererKey = EXTERNAL_FORM_RENDERER_KEYS.includes(value) ? value : "contact-popup";
      this.form.rendererKey = rendererKey;
      this.form.displayConfig.rendererKey = rendererKey;
      if (INLINE_RENDERER_KEYS.includes(rendererKey)) {
        this.form.displayConfig.delaySeconds = 0;
        this.form.displayConfig.repeatMode = "always";
      }
    },
    async saveWidget() {
      if (this.saveDisabled) return;
      this.saving = true;
      this.error = "";
      this.success = "";
      try {
        this.ensureFormKey();
        this.syncArraysFromText();
        const payload = buildExternalFormWidgetPayload(this.form);
        const { data } = this.form.id
          ? await updateExternalFormWidget(this.form.id, payload)
          : await createExternalFormWidget(payload);
        const saved = normalizeExternalFormWidget(data?.widget, {
          workflowInstanceId: this.workflowInstanceId,
          endpointId: this.endpoints[0]?.id || null,
        });
        const index = this.widgets.findIndex((widget) => Number(widget.id) === Number(saved.id));
        if (index >= 0) this.widgets.splice(index, 1, saved);
        else this.widgets.unshift(saved);
        this.selectWidget(saved.id);
        this.success = this.$t("ncrm.workflows.nodeSettings.externalForms.saveSuccess");
        this.$emit("saved", saved);
      } catch (error) {
        // Codurile Privacy (S5.1) au text localizat; restul păstrează mesajul serverului.
        const code = error?.response?.data?.code;
        this.error = PRIVACY_ISSUE_CODES.includes(code)
          ? this.$t(`ncrm.workflows.nodeSettings.externalForms.privacy.issues.${code}`)
          : error?.response?.data?.error || this.$t("ncrm.workflows.nodeSettings.externalForms.saveError");
      } finally {
        this.saving = false;
      }
    },
    previewInlineForm() {
      if (!this.canPreviewInline) return;
      const basePath = RENDERER_WIDGET_PATHS[this.form.rendererKey] || RENDERER_WIDGET_PATHS[DEFAULT_EXTERNAL_FORM_RENDERER_KEY];
      const locale = this.isFileRequestRenderer ? this.form.fileRequestPreviewLocale
        : this.isDataCollectionRenderer ? this.form.dataCollectionPreviewLocale : "";
      const localeQuery = this.isFileRequestRenderer || this.isDataCollectionRenderer ? `?locale=${encodeURIComponent(locale || "ro")}` : "";
      window.open(
        `${basePath}/${encodeURIComponent(this.form.form_key)}/preview${localeQuery}`,
        "_blank",
        "noopener,noreferrer"
      );
    },
  },
};
</script>

<style scoped src="./NcrmWorkflowExternalFormsSettings.css"></style>
